import { useQuery } from '@tanstack/react-query'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { ErrorState } from '@/components/ui/ErrorState'
import { Modal } from '@/components/ui/Modal'
import { LoadingPanel } from '@/components/ui/Spinner'
import { api, exportUrl, type SearchQuery } from '@/lib/apiClient'
import { copyToClipboard, downloadUrl } from '@/lib/download'
import { formatNumber } from '@/lib/format'
import { useToast } from '@/hooks/useToast'

const PREVIEW_LIMIT = 100

export interface DataPreviewModalProps {
  format: 'csv' | 'json' | null
  query: Omit<SearchQuery, 'page' | 'pageSize'>
  totalItems: number
  onClose: () => void
}

interface JsonPreview {
  count: number
  completeness?: { is_complete: boolean; verdict: string }
  judgments: unknown[]
}

/** In-app viewer for the exact bytes the download would contain. */
export function DataPreviewModal({
  format,
  query,
  totalItems,
  onClose,
}: DataPreviewModalProps) {
  const { push } = useToast()

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['export-preview', format, query],
    queryFn: () => api.previewExport(query, format as 'csv' | 'json', PREVIEW_LIMIT),
    enabled: format !== null,
  })

  const text =
    format === 'csv'
      ? typeof data === 'string'
        ? data
        : ''
      : data
        ? JSON.stringify(data, null, 2)
        : ''

  const jsonPreview = format === 'json' ? (data as JsonPreview | undefined) : undefined
  const truncated = totalItems > PREVIEW_LIMIT

  const onCopy = async () => {
    const ok = await copyToClipboard(text)
    push({
      tone: ok ? 'success' : 'error',
      title: ok ? 'Copied to clipboard' : 'Could not copy',
    })
  }

  return (
    <Modal
      open={format !== null}
      onClose={onClose}
      title={`${(format ?? '').toUpperCase()} preview`}
      description={
        truncated
          ? `First ${PREVIEW_LIMIT} of ${formatNumber(totalItems)} records. The download contains every match.`
          : `${formatNumber(totalItems)} record${totalItems === 1 ? '' : 's'} matching the current filters.`
      }
      footer={
        <>
          <Button onClick={() => void onCopy()} disabled={!text}>
            Copy
          </Button>
          <Button
            variant="primary"
            onClick={() => downloadUrl(exportUrl(format ?? 'csv', query))}
          >
            Download full file
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 p-4">
        {jsonPreview?.completeness && !jsonPreview.completeness.is_complete ? (
          <Alert tone="warning" title="Incomplete data set">
            {jsonPreview.completeness.verdict}
          </Alert>
        ) : null}

        {isLoading ? <LoadingPanel label="Rendering preview…" /> : null}

        {isError ? (
          <ErrorState
            variant="inline"
            error={error}
            fallbackTitle="Could not build the preview"
          />
        ) : null}

        {text ? (
          <pre className="scrollbar-slim max-h-[55vh] overflow-auto rounded-md border border-line bg-surface-sunken p-3 font-mono text-xs leading-relaxed text-ink">
            {text}
          </pre>
        ) : null}
      </div>
    </Modal>
  )
}
